import { useCallback } from 'react';
import type { AnswerSheetBySection } from '@examflow/core';

export interface AnswerSheetProps {
  total: number;
  currentIndex: number;
  answeredSet: Set<number>;
  markedSet: Set<number>;
  onSelectQuestion: (index: number) => void;
  title?: string;
  /** 多模块训练：按 section 分块展示，传入时使用分块布局 */
  sections?: AnswerSheetBySection;
}

const ITEMS_PER_ROW = 5;

function getItemClass(
  index: number,
  currentIndex: number,
  answeredSet: Set<number>,
  markedSet: Set<number>
): string {
  const isCurrent = index === currentIndex;
  const isAnswered = answeredSet.has(index);
  const isMarked = markedSet.has(index);

  const base =
    'flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors ';

  if (isCurrent) {
    return (
      base + 'bg-primary-600 text-white ring-2 ring-primary-500 ring-offset-2 ring-offset-white'
    );
  }
  if (isMarked) {
    return base + 'bg-warning-100 text-warning-700 hover:bg-warning-200';
  }
  if (isAnswered) {
    return base + 'bg-success-100 text-success-700 hover:bg-success-200';
  }
  return base + 'bg-slate-100 text-slate-600 hover:bg-slate-200';
}

export function AnswerSheet({
  total,
  currentIndex,
  answeredSet,
  markedSet,
  onSelectQuestion,
  title = '答题卡',
  sections,
}: AnswerSheetProps) {
  const getItemClassCb = useCallback(
    (index: number) => getItemClass(index, currentIndex, answeredSet, markedSet),
    [currentIndex, answeredSet, markedSet]
  );

  // 按 section 分块布局
  if (sections && sections.sections.length > 0) {
    return (
      <aside className="w-full flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm lg:sticky lg:top-32 lg:w-72 lg:self-start">
        <div className="px-3 py-2.5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
        </div>
        <div className="p-3 max-h-[calc(100vh-12rem)] overflow-y-auto">
          <div className="space-y-4">
            {sections.sections.map((sec) => (
              <div key={sec.sectionId}>
                <div
                  className="text-xs font-medium text-slate-500 mb-1.5 truncate"
                  title={sec.sectionName}
                >
                  {sec.sectionName}
                </div>
                <div className="flex flex-wrap gap-2">
                  {sec.items.map((item) => (
                    <button
                      key={item.index}
                      type="button"
                      onClick={() => onSelectQuestion(item.index)}
                      className={getItemClassCb(item.index)}
                      title={`第 ${item.index + 1} 题`}
                    >
                      {item.index + 1}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="h-4 w-4 rounded bg-success-100 ring-1 ring-success-200" />
              已答
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-4 w-4 rounded bg-warning-100 ring-1 ring-warning-200" />
              待检查
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-4 w-4 rounded bg-slate-100 ring-1 ring-slate-200" />
              未答
            </span>
          </div>
        </div>
      </aside>
    );
  }

  // 扁平布局（练习模式或未传 sections）
  const rows: number[][] = [];
  for (let i = 0; i < total; i += ITEMS_PER_ROW) {
    rows.push(Array.from({ length: Math.min(ITEMS_PER_ROW, total - i) }, (_, j) => i + j));
  }

  return (
    <aside className="w-full flex-shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm lg:sticky lg:top-32 lg:w-72 lg:self-start">
      <div className="px-3 py-2.5 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      <div className="p-3 max-h-[calc(100vh-12rem)] overflow-y-auto">
        <div className="space-y-2">
          {rows.map((row, rowIdx) => (
            <div key={rowIdx} className="flex flex-wrap gap-2">
              {row.map((idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onSelectQuestion(idx)}
                  className={getItemClassCb(idx)}
                  title={`第 ${idx + 1} 题`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="h-4 w-4 rounded bg-success-100 ring-1 ring-success-200" />
            已答
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-4 w-4 rounded bg-warning-100 ring-1 ring-warning-200" />
            待检查
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-4 w-4 rounded bg-slate-100 ring-1 ring-slate-200" />
            未答
          </span>
        </div>
      </div>
    </aside>
  );
}
